import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Table, Button, Input, Popconfirm } from "antd";
import { PlusOutlined, SaveOutlined, CloseOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/delete-icon.svg";
import _ from "lodash";
import "./stockTakeTable.less"; // Import specific CSS file
import * as actions from "../../redux/actions";
import { useLabsStock } from "../../context/LabsStockContext";
import { useSelector, useDispatch } from "react-redux";
import { getExpiryColor } from "../../utils";

const StockTakeTable = ({ recordId }) => {
  const dispatch = useDispatch();
  const { currentStockManagementRegister } = useSelector((state) => state.labsStockManagementReducer);
  const {
    reportsGenerated,
    varianceReports,
    isAmending,
    setIsAmending,
    stockTakeDataMap,
    updateStockTakeData,
    addStockTakeItem,
    handleCompleteStockTake,
    calculateVarianceData,
    updateVarianceReport,
    getExistingVarianceReportList,
    setVarianceReportId,
    getVarianceReportId,
    varianceReportMap,
    createStockTake,
    deleteStockTake,
    isCreatingStockTake,
    finalizeRegister,
    wareHouseId,
    stockTakeErrors,
    clearStockTakeError,
    generateVarianceReport,
  } = useLabsStock();

  const isVarianceGenerated =
    reportsGenerated[recordId] ||
    (Array.isArray(varianceReports[recordId]) && varianceReports[recordId].length > 0);

  const data = stockTakeDataMap[recordId] || [];
  const setData = (newData) => updateStockTakeData(recordId, newData);

  const [sortedInfo, setSortedInfo] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [isAddMode, setIsAddMode] = useState(true);
  const [barcode, setBarcode] = useState("");
  const [search, setSearch] = useState({});
  const [req, setReq] = useState(false);
  const [page, setPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const inputRef = useRef(null);

  const [editingKey, setEditingKey] = useState("");
  const [scanError, setScanError] = useState(false);
  const [editingValues, setEditingValues] = useState({});

  useEffect(() => {
    if (isAddMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddMode, data.length]);

  useEffect(() => {
    if (stockTakeErrors && stockTakeErrors[recordId]) {
      setScanError(true);
    }
  }, [stockTakeErrors, recordId]);

  const prevParamsRef = useRef({});

  useEffect(() => {
    if (!recordId) return;

    const fieldMapping = {
      'scannedCode': 'scannedCode',
      'lotNo': 'lotNo',
      'expiryDate': 'expiryDate',
      'daysPastExpiry': 'daysPastExpiry',
      'barCode': 'barcode', 
      'stockItemLookup': 'stockItemLookup',
      'level': 'level'
    };

    const params = {
      ...search,
      ...(sortedInfo?.columnKey && sortedInfo?.order && {
        sortBy: fieldMapping[sortedInfo.columnKey] || sortedInfo.columnKey,
        sortOrder: sortedInfo.order === 'ascend' ? 'asc' : 'desc'
      })
    }

    const prevParams = prevParamsRef.current;
    if (JSON.stringify(prevParams) === JSON.stringify(params)) return;
    prevParamsRef.current = params;

    const timeoutId = setTimeout(() => {
      dispatch(actions.fetchAllStockTakes(recordId, params))
    }, 300);

    return () => clearTimeout(timeoutId);

  }, [sortedInfo, recordId, search])

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 1000)
  );

  const handleAdd = () => {
    setIsAddMode(true);
  };

  const handleCancel = () => {
    setIsAddMode(false);
    setBarcode("");
    setScanError(false);
    if (stockTakeErrors && stockTakeErrors[recordId]) {
      clearStockTakeError(recordId);
    }
  };

  const handleSave = () => {
    if (!barcode.trim() || isCreatingStockTake) return;
    // Clear any prior error before retrying
    if (scanError) {
      setScanError(false);
      clearStockTakeError(recordId);
    }

    const stockTakeData = {
      scannedCode: barcode,
      warehouseId: wareHouseId,
    };

    createStockTake(recordId, stockTakeData);
    finalizeRegister(recordId);
    setBarcode("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isCreatingStockTake) {
      handleSave();
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    console.log('Sorting triggered:', { sorter, sortedInfo });
    setSortedInfo(sorter);
    debouncedCallApi();
  };

  const isEditing = (record) => record.key === editingKey;

  const handleEdit = (record) => {
    setEditingKey(record.key);
    setEditingValues({ ...record });
  };

  const handleDelete = (record) => {
    const stockTakeId = record.id;
    deleteStockTake(stockTakeId, recordId);
  };

  const handleSaveEdit = (key) => {
    const record = tableData.find(item => item.key === key);
    if (record && record.id) {
      const updatedData = data.map(item =>
        item.key === key ? { ...item, ...editingValues } : item
      );
      setData(updatedData);
    }
    setEditingKey("");
    setEditingValues({});
  };

  const handleCancelEdit = () => {
    setEditingKey("");
    setEditingValues({});
  };

  const getRegisterId = () => {
    if (currentStockManagementRegister?.id) {
      return currentStockManagementRegister.id;
    }
    return recordId;
  };

  const initialCountRef = useRef(null);

  useEffect(() => {
    if (isAmending) {
      if (initialCountRef.current === null) {
        initialCountRef.current = data.length;
      }
    } else {
      initialCountRef.current = null;
    }
  }, [isAmending]);

  const handleComplete = () => {
    generateVarianceReport(recordId);
    if (setIsAmending) {
      setIsAmending(false);
    }
    handleCompleteStockTake(recordId);
  };


  const columns = [
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Scanned Code</div>
          </div>
        );
      },
      dataIndex: "scannedCode",
      key: "scannedCode",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "scannedCode" && sortedInfo.order,
      width: "20%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            children: (
              <div style={{ paddingBottom: scanError ? "5px" : "0" }}>
                <Row align="middle" gutter={8} wrap={false} style={{ padding: "5px", borderRadius: "5px" }}>
                  <Col flex="auto" style={{ position: "relative" }}>
                    <Input
                      ref={inputRef}
                      placeholder="Add Scanned Code"
                      value={barcode}
                      onChange={(e) => {
                        setBarcode(e.target.value);
                        if (scanError) {
                          setScanError(false);
                          clearStockTakeError(recordId);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      bordered={false}
                      style={scanError ? { color: "#f25003", fontWeight: 600 } : {}}
                    />
                  </Col>
                  <Col style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <Row style={{ display: "inline-flex", gap: "10px", alignItems: "center" }}>
                      {!scanError && (
                        <SaveOutlined
                          onClick={handleSave}
                          className="stock-action-icon"
                          style={{
                            color: isCreatingStockTake ? "#ccc" : "#ed017f",
                            fontSize: "18px",
                            cursor: isCreatingStockTake ? "not-allowed" : "pointer"
                          }}
                        />
                      )}
                      <CloseOutlined onClick={handleCancel} className="stock-action-icon" style={{ color: "#ed017f", fontSize: "18px", cursor: "pointer" }} />
                    </Row>
                  </Col>
                </Row>
                {scanError && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 11px 2px",
                    marginLeft: "20px",
                    marginTop: "-2px",
                    height: "42px",
                    color: "white",
                    fontSize: "13px",
                    fontFamily: "poppins, Arial",
                    lineHeight: 1.4,
                    backgroundColor: "#373435",
                    borderRadius: "2px",
                    border: "1px solid #f25003",
                    width: "max-content",
                    maxWidth: "100%",
                  }}>
                    <ExclamationCircleFilled style={{ fontSize: "13px", flexShrink: 0, color: "#e20f00" }} />
                    <span>This scanned code is not recognized. Please check the scanned code and try again.</span>
                  </div>
                )}
              </div>
            ),
            props: {
              colSpan: 8,
            },
          };
        }
        return (
          <div style={{ wordBreak: "break-all", whiteSpace: "normal" }}>
            {text}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Lot No</div>
          </div>
        );
      },
      dataIndex: "lotNo",
      key: "lotNo",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "lotNo" && sortedInfo.order,
      width: "12%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return text;
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Expiry Date</div>
          </div>
        );
      },
      dataIndex: "expiryDate",
      key: "expiryDate",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "expiryDate" && sortedInfo.order,
      width: "12%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        const color = getExpiryColor(text);
        return color ? (
          <span className={`expiry-highlight expiry-highlight-${color}`}>{text}</span>
        ) : (
          text
        );
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Days Until Expiry</div>
          </div>
        );
      },
      dataIndex: "daysPastExpiry",
      key: "daysPastExpiry",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "daysPastExpiry" && sortedInfo.order,
      width: "10%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return text;
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Bar Code</div>
          </div>
        );
      },
      dataIndex: "barCode",
      key: "barCode",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "barCode" && sortedInfo.order,
      width: "18%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return text;
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Stock Code</div>
          </div>
        );
      },
      dataIndex: "stockItemLookup",
      key: "stockItemLookup",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "stockItemLookup" && sortedInfo.order,
      width: "18%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return text;
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text">Level</div>
          </div>
        );
      },
      dataIndex: "level",
      key: "level",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "level" && sortedInfo.order,
      width: "6%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        if (isEditing(record)) {
          return (
            <Input
              value={editingValues.level}
              onChange={(e) => setEditingValues({ ...editingValues, level: e.target.value })}
              style={{ width: "60px" }}
            />
          );
        }
        return text;
      },
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="stock-table-header-text"></div>
          </div>
        );
      },
      key: "actions",
      width: "5%",
      render: (text, record) => {
        if (record.isAddRow) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }

        if (isEditing(record)) {
          return (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <SaveOutlined
                onClick={() => handleSaveEdit(record.key)}
                className="stock-action-icon"
                style={{ color: "#ed017f", fontSize: "18px", cursor: "pointer" }}
              />
              <CloseOutlined
                onClick={() => handleCancelEdit()}
                className="stock-action-icon"
                style={{ color: "#ed017f", fontSize: "18px", cursor: "pointer" }}
              />
            </div>
          );
        }

        return (!isVarianceGenerated || isAmending) ? (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }} className="stock-take-actions">
            {/* <EditIcon
              className="custom-icon"
              onClick={() => handleEdit(record)}
              style={{ cursor: "pointer" }}
            /> */}
            <Popconfirm
              title="Are you sure you want to delete this item?"
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
              placement="bottom"
            >
              <DeleteIcon
                className="custom-icon"
                style={{ cursor: "pointer" }}
              />
            </Popconfirm>
          </div>
        ) : null;
      },
    },
  ];

  const tableData = isAddMode && (!isVarianceGenerated || isAmending) ? [{ key: "add_row", isAddRow: true }, ...data] : data;
  const filteredColumns = columns; // Actions column only shows when table is unlocked

  return (
    <Row className="stock-doc-control-parent-row stock-take-table-container">
      <Col span={24} className="stock-table-heading-col">
        <Row align="middle" gutter={[50, 0]} justify="space-between">
          <Col>
            <Row gutter={[50, 0]} align="middle">
              <Col>
                <div>
                  <span className="heading-span">Stock Take</span>
                </div>
              </Col>
              {(!isVarianceGenerated || isAmending) && (
                <Col>
                  <Button
                    icon={<PlusOutlined />}
                    className="add-doc-btn"
                    size="middle"
                    onClick={handleAdd}
                    disabled={isAddMode}
                  >
                    ADD
                  </Button>
                </Col>
              )}
              {(!isVarianceGenerated || isAmending) && (isAddMode || data.length > 0) && (
                <Col>
                  <Button
                    className="add-doc-btn"
                    size="middle"
                    onClick={handleComplete}
                    style={{ width: "180px" }}
                  >
                    Complete Stock Take
                  </Button>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Table
          scroll={{ y: '350px' }}
          className={`special-table stock-table ims-doc-table reduced-padding-table`}
          rowClassName="editable-row"
          columns={filteredColumns}
          dataSource={tableData}
          pagination={false}
          onChange={handleChange}
        />
      </Col>
    </Row>
  );
};

export default StockTakeTable;
